import sendFBPixelEvent from '@apps/www/src/www/helpers/sendFBPixelEvent';
import sendGAEvent, { isGAReady } from '@apps/www/src/www/helpers/sendGAEvent';
import useAuthUserID from '@apps/www/src/www/hooks/useAuthUserID';
import config from '@pkgs/shared-client/config';
import BillingCycle from '@pkgs/shared/enums/BillingCycle';
import SubscriptionTier from '@pkgs/shared/enums/SubscriptionTier';
import { getPlanFromPlanID, getPlanMonthlyPrice } from '@pkgs/shared/plans';
import { useMemo } from 'react';

interface GAEcommerceItem {
	item_id: string;
	item_name: string;
	item_category: string;
	item_category2?: string;
	price: number;
	quantity: number;
}

interface FBPixelContent {
	id: string;
	quantity: number;
	item_price: number;
}

let globalUserID: string | undefined = undefined;

const TRACKING_FUNCTIONS = {
	trackPageView: () => {
		// Only FB Pixel since Next.js GA integration already tracks page views.
		sendFBPixelEvent('track', 'PageView');
	},
	trackLogin: (isJoin: boolean) => {
		if (isJoin) {
			sendGAEvent('event', 'join', {
				user_id_dimension: globalUserID,
			});
			sendFBPixelEvent('track', 'CompleteRegistration');
		} else {
			sendGAEvent('event', 'login', {
				user_id_dimension: globalUserID,
			});
			sendFBPixelEvent('track', 'Login');
		}
	},

	trackLogout: () => {
		sendGAEvent('event', 'logout', {
			user_id_dimension: globalUserID,
		});
	},

	trackHomeViewLoggedIn: () => {
		sendGAEvent('event', 'home_view_logged_in', {
			user_id_dimension: globalUserID,
		});
	},

	trackHomeViewLoggedOut: () => {
		sendGAEvent('event', 'home_view_logged_out');
	},

	trackSubscriptionPurchase: ({
		planId,
		tier,
		billingCycle,
		amountInDollars,
	}: {
		planId: string;
		tier: ValueOf<typeof SubscriptionTier>;
		billingCycle: ValueOf<typeof BillingCycle>;
		amountInDollars: number;
	}) => {
		const currency = 'USD';

		const item: GAEcommerceItem = {
			item_id: planId,
			item_name: `${tier} - ${billingCycle === BillingCycle.MONTHLY ? 'Monthly' : 'Annual'}`,
			item_category: 'Subscription',
			item_category2: tier,
			price: amountInDollars,
			quantity: 1,
		};

		sendGAEvent('event', 'purchase', {
			transaction_id: `${planId}_${Date.now()}`,
			value: amountInDollars,
			currency,
			items: [item],
			user_id_dimension: globalUserID,
			subscription_tier: tier,
			billing_cycle: billingCycle,
		});

		const contents: FBPixelContent[] = [
			{
				id: planId,
				quantity: 1,
				item_price: amountInDollars,
			},
		];

		const plan = getPlanFromPlanID(config, planId);
		const monthlyPrice = getPlanMonthlyPrice(plan);
		const estimatedLTV = monthlyPrice * 18; // 18 months of subscription on average

		sendFBPixelEvent('track', 'Subscribe', {
			value: amountInDollars,
			currency,
			predicted_ltv: estimatedLTV,
			content_ids: [planId],
			content_type: 'product',
			contents,
			custom_data: {
				subscription_tier: tier,
				billing_cycle: billingCycle,
			},
		});
	},

	trackCoursePurchase: ({
		amountInCents,
		currency = 'USD',
	}: {
		amountInCents: number;
		currency?: string;
	}) => {
		const item: GAEcommerceItem = {
			item_id: 'savee_course',
			item_name: 'Savee Course',
			item_category: 'Course',
			item_category2: 'Education',
			price: amountInCents / 100,
			quantity: 1,
		};

		sendGAEvent('event', 'purchase', {
			transaction_id: `course_${Date.now()}`,
			value: amountInCents / 100,
			currency,
			items: [item],
			user_id_dimension: globalUserID,
			product_type: 'course',
		});

		const contents: FBPixelContent[] = [
			{
				id: 'savee_course',
				quantity: 1,
				item_price: amountInCents / 100,
			},
		];

		sendFBPixelEvent('track', 'Purchase', {
			value: amountInCents / 100,
			currency,
			content_ids: ['savee_course'],
			content_type: 'product',
			contents,
			custom_data: {
				product_type: 'course',
			},
		});
	},

	trackBeginCheckout: ({
		planId,
		tier,
		billingCycle,
		price,
	}: {
		planId: string;
		tier: ValueOf<typeof SubscriptionTier>;
		billingCycle: ValueOf<typeof BillingCycle>;
		price: number;
	}) => {
		const item: GAEcommerceItem = {
			item_id: planId,
			item_name: `${tier} - ${billingCycle === BillingCycle.MONTHLY ? 'Monthly' : 'Annual'}`,
			item_category: 'Subscription',
			item_category2: tier,
			price,
			quantity: 1,
		};

		sendGAEvent('event', 'begin_checkout', {
			currency: 'USD',
			value: price,
			items: [item],
			user_id_dimension: globalUserID,
			subscription_tier: tier,
			billing_cycle: billingCycle,
		});

		const contents: FBPixelContent[] = [
			{
				id: planId,
				quantity: 1,
				item_price: price,
			},
		];

		sendFBPixelEvent('track', 'InitiateCheckout', {
			value: price,
			currency: 'USD',
			content_ids: [planId],
			content_type: 'product',
			contents,
			custom_data: {
				subscription_tier: tier,
				billing_cycle: billingCycle,
			},
		});
	},
} as const;

function isTrackingReady() {
	const gaReady = !config.googleAnalytics.enabled || isGAReady();

	return gaReady;
}

type TrackingFunctionName = keyof typeof TRACKING_FUNCTIONS;

interface QueuedTrackingEvent {
	functionName: TrackingFunctionName;
	args: any[];
}

const queue: QueuedTrackingEvent[] = [];

function processQueue() {
	if (queue.length === 0) {
		return;
	}

	if (!isTrackingReady()) {
		// Try again in 500ms
		setTimeout(processQueue, 500);

		return;
	}

	const toProcess = [...queue];
	queue.length = 0;

	toProcess.forEach(({ functionName, args }) => {
		// @ts-expect-error ok
		TRACKING_FUNCTIONS[functionName](...args);
	});
}

function addToQueue(functionName: TrackingFunctionName, args: any[]) {
	queue.push({ functionName, args });

	processQueue();
}

export default function useTracking() {
	const userID = useAuthUserID();

	// Only 1 userID at a time on the app, safe to store as a global variable to be used on the tracking functions.
	globalUserID = userID;

	const functions = useMemo(() => {
		const trackPageView: typeof TRACKING_FUNCTIONS.trackPageView = (...args) => {
			addToQueue('trackPageView', args);
		};

		const trackLogin: typeof TRACKING_FUNCTIONS.trackLogin = (...args) => {
			addToQueue('trackLogin', args);
		};

		const trackLogout: typeof TRACKING_FUNCTIONS.trackLogout = (...args) => {
			addToQueue('trackLogout', args);
		};

		const trackHomeViewLoggedIn: typeof TRACKING_FUNCTIONS.trackHomeViewLoggedIn = (
			...args
		) => {
			addToQueue('trackHomeViewLoggedIn', args);
		};

		const trackHomeViewLoggedOut: typeof TRACKING_FUNCTIONS.trackHomeViewLoggedOut = (
			...args
		) => {
			addToQueue('trackHomeViewLoggedOut', args);
		};

		const trackSubscriptionPurchase: typeof TRACKING_FUNCTIONS.trackSubscriptionPurchase = (
			...args
		) => {
			addToQueue('trackSubscriptionPurchase', args);
		};

		const trackCoursePurchase: typeof TRACKING_FUNCTIONS.trackCoursePurchase = (...args) => {
			addToQueue('trackCoursePurchase', args);
		};

		const trackBeginCheckout: typeof TRACKING_FUNCTIONS.trackBeginCheckout = (...args) => {
			addToQueue('trackBeginCheckout', args);
		};

		return {
			trackPageView,
			trackLogin,
			trackLogout,
			trackHomeViewLoggedIn,
			trackHomeViewLoggedOut,
			trackSubscriptionPurchase,
			trackCoursePurchase,
			trackBeginCheckout,
		};
	}, []);

	return functions;
}
